import { useEffect } from 'react';

import { LocalAccessRoles } from '@shared/config';
import { useAppDispatch } from 'application/providers/StoreProvider';
import { setUserData, setAuthToken } from 'application/store';

import { setTokenCookie } from '@shared/lib';
import { useParams, useURLSearchParams } from 'application/providers/RouterProvider';
import { IApplicationPage } from 'application/pages/types';
import { REDIRECT_PAGE_PATH } from 'application/consts';
import AnalyticsService, {
  EventNameAMP,
  EventNameGTM,
  LoginPathValues,
  ParamNameAMP,
  ParamNameGTM,
} from 'application/services/AnalyticsService';

import { useUtmMarks, useCurrentUser } from '@entities/auth';

import DateUtils from '@zvuk-b2b/react-uikit/toolkit/DateUtils';

enum RedirectQueryKeys {
  TOKEN = 'token',
  MODE = 'mode',
  EVENT = 'event',
}

enum RedirectModeQueryValues {
  REGISTER = 'registration',
  LOGIN = 'login',
}

type RedirectPageParamsType = {
  from?: string;
};

const RedirectPage: IApplicationPage = () => {
  const dispatch = useAppDispatch();

  const [utmRequest] = useUtmMarks();
  const [userRequest] = useCurrentUser();

  const pathParams = useParams<RedirectPageParamsType>();
  const [authToken, mode, event] = useURLSearchParams([
    RedirectQueryKeys.TOKEN,
    RedirectQueryKeys.MODE,
    RedirectQueryKeys.EVENT,
  ]);

  useEffect(() => {
    if (pathParams.from === 'sbbol') {
      AnalyticsService.eventGTM(EventNameGTM.UI_ACTION, {
        [ParamNameGTM.UI_ACTION_KEY]: EventNameAMP.USER_SIGNED_UP_SBER_ID,
        [ParamNameGTM.ACCOUNT_CREATED_DATE]: DateUtils.getDate(),
      });
    }
  }, [pathParams]);

  useEffect(() => {
    if (mode === RedirectModeQueryValues.REGISTER) {
      AnalyticsService.event(EventNameAMP.USER_SIGNED_UP);

      AnalyticsService.eventGTM(EventNameGTM.UI_ACTION, {
        [ParamNameGTM.UI_ACTION_KEY]: event || EventNameAMP.USER_SIGNED_UP,
        [ParamNameGTM.ACCOUNT_CREATED_DATE]: DateUtils.getDate(),
      });
    }

    if (mode === RedirectModeQueryValues.LOGIN) {
      AnalyticsService.event(EventNameAMP.USER_LOGGED_IN, {
        [ParamNameAMP.LOG_IN_PATH]: LoginPathValues.SBBID,
      });
    }
  }, [mode]);

  const initUserData = async () => {
    setTokenCookie(authToken!);
    const userData = await userRequest();
    dispatch(setAuthToken(authToken ?? undefined));
    dispatch(setUserData(userData));

    if (mode === RedirectModeQueryValues.LOGIN) {
      AnalyticsService.eventGTM(EventNameGTM.AUTH_SUCCESS, {
        [ParamNameGTM.ACCOUNT_CREATED_DATE]: userData?.mainCompany?.created,
      });
    }
  };

  useEffect(() => {
    if (authToken) {
      initUserData();
    }

    return () => {
      const isRedirectFromRegistration = mode === RedirectModeQueryValues.REGISTER;

      if (authToken && isRedirectFromRegistration) {
        utmRequest();
      }
    };
  }, [authToken]);

  return null;
};

RedirectPage.config = {
  routes: [{ path: REDIRECT_PAGE_PATH }],
  access: [LocalAccessRoles.ANONYMOUS],
};

export default RedirectPage;
